import { ApiErrorState } from '@/components/ApiErrorState/ApiErrorState'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { DeleteClusterButton } from '@/components/DeleteClusterButton/DeleteClusterButton'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { useCluster } from '@/contexts/useCluster'
import { auditLogsRoute } from '@/features/AuditLogs/pages/AuditLogsPage/AuditLogsPage'
import { HiveDataCard } from '@/features/HivesOverview/components/HiveDataCard'
import { useFindHivesByOrgId } from '@/generated/http-clients/v2ApiComponents'
import { hivesOverviewRootRoute } from '@/router/rootRoutes'
import { Button, HStack, Icon, Skeleton } from '@chakra-ui/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Link, createRoute } from '@tanstack/react-router'
import { FileText } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { hivesOverviewOrganizationDetailsRoute } from './HivesOverviewOrganizationDetails'

export const hivesOverviewClusterDetailsRoute = createRoute({
  getParentRoute: () => hivesOverviewRootRoute,
  path: '/orgs/$orgId/$clusterId',
  component: HivesOverviewsClusterDetails,
})

export function HivesOverviewsClusterDetails() {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const { t: tFeatures } = useTranslation('features', { useSuspense: false })
  const pathParams = hivesOverviewClusterDetailsRoute.useParams()
  const clusterContext = useCluster()

  const hivesRequest = useFindHivesByOrgId({
    pathParams: {
      orgId: pathParams.orgId,
    },
  })

  const currentHive = useMemo(() => {
    return hivesRequest.data?.hives_info?.find(
      (hive) => hive.cluster_id === pathParams.clusterId,
    )
  }, [hivesRequest.data?.hives_info, pathParams.clusterId])

  // Name the org and the cluster in the trail rather than repeating two opaque
  // ids — "Acme GmbH / production-eu" says where you are, `org_a1b2 / c3d4e5`
  // does not. Both fall back to their id while the request is in flight, or
  // when the org has no company name and the cluster no user-given name.
  const orgName = hivesRequest.data?.company_name?.trim()
  const clusterName = currentHive?.cluster_name?.trim()

  const breadCrumbs = [
    {
      name: String(t('sidebar.runningHives')),
      path: hivesOverviewRootRoute.to,
    },
    {
      name: orgName || pathParams.orgId,
      // `to` is the route pattern (`/orgs/$orgId`); `BreadCrumbs` renders a
      // plain `<Link to={path}>`, so the param has to be filled in here.
      path:
        hivesOverviewOrganizationDetailsRoute.to?.replace(
          '$orgId',
          pathParams.orgId,
        ) ?? `/orgs/${pathParams.orgId}`,
    },
    {
      name: clusterName || pathParams.clusterId,
      path: '',
    },
  ]

  return (
    <PageLayout
      title="Cluster Overview"
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={
        <HStack gap={2}>
          <Link
            to={auditLogsRoute.to}
            search={{ hiveId: pathParams.clusterId, dateRange: 'last24Hours' }}
          >
            <Button variant="subtle" size="sm">
              <Icon as={FileText} />
              {String(tFeatures('auditLogs.contextualAccess.viewAuditLogs'))}
            </Button>
          </Link>
          {/* Deletion sits with the other page actions instead of in a danger
              zone card below the fold. The name-confirmation modal is what
              guards the action; the card only added vertical noise.
              `clusterName` is the same string the breadcrumb shows, so the
              modal asks the operator to retype something actually on screen —
              it used to be handed `cluster_id` regardless. Undefined when the
              cluster is unnamed, which the button falls back to the id for. */}
          <DeleteClusterButton
            orgId={pathParams.orgId}
            clusterId={pathParams.clusterId}
            clusterName={clusterName || undefined}
            onDelete={clusterContext.deleteOrgCluster}
          />
        </HStack>
      }
    >
      {hivesRequest.isLoading ? <Skeleton h="500px" w="100%" /> : null}
      {hivesRequest.isError || !hivesRequest.data ? (
        <ApiErrorState
          apiResource={hivesRequest as unknown as UseQueryResult}
          errorTitle="An error happened"
          errorDescription="We failed to load the data from the server for the Hive Details."
        />
      ) : null}
      {currentHive && (
        <HiveDataCard
          hive={currentHive}
          orgId={pathParams.orgId}
          stripeId={hivesRequest.data?.stripe_id}
          users={hivesRequest.data?.users_info}
        />
      )}
    </PageLayout>
  )
}
